/**
 * Vocabulario de posiciones y lectura de formaciones — secciones 8.3 y 8.4.
 *
 * `Position` se declara aquí porque es lo primero que necesita la interfaz y `api/types.ts`
 * todavía no existe (llega con M7-T02). Cuando llegue, que lo reexporte desde este fichero en
 * lugar de volver a escribirlo: el chip de posición y el contrato HTTP tienen que hablar del
 * mismo tipo o dejarán de cuadrar en silencio.
 */

export type Position = "POR" | "DEF" | "MED" | "DEL";

/** Orden canónico: el del campo, de la portería hacia arriba. */
export const POSITIONS: readonly Position[] = ["POR", "DEF", "MED", "DEL"];

/** Nombre completo en singular, para etiquetas accesibles. */
export const POSITION_NAMES: Record<Position, string> = {
  POR: "Portero",
  DEF: "Defensa",
  MED: "Centrocampista",
  DEL: "Delantero",
};

/** Encabezado de grupo en las listas por posición. */
export const POSITION_GROUPS: Record<Position, string> = {
  POR: "Porteros",
  DEF: "Defensas",
  MED: "Centrocampistas",
  DEL: "Delanteros",
};

export function isPosition(value: unknown): value is Position {
  return typeof value === "string" && POSITIONS.includes(value as Position);
}

/** Una línea del campo: cuántos jugadores y de qué posición. */
export interface FormationLine {
  position: Position;
  count: number;
}

export interface Formation {
  /** La cadena tal cual viene del servidor (`league.formations`). */
  raw: string;
  /** De la portería hacia arriba: POR, DEF, MED, DEL. */
  lines: FormationLine[];
  counts: Record<Position, number>;
}

/** Las once fichas del campo: un portero y los diez de las tres líneas de la formación. */
export const XI_SIZE = 11;

/**
 * Lee `"4-3-3"` y devuelve las cuatro líneas del campo.
 *
 * Devuelve `null` —y no una formación a medias— si la cadena no son tres números que sumen diez:
 * el campo prefiere no dibujarse a colocar once fichas en sitios inventados. El portero nunca
 * aparece en la cadena porque siempre es uno.
 */
export function parseFormation(raw: string): Formation | null {
  const partes = raw.trim().split("-");
  if (partes.length !== 3) return null;

  const numeros = partes.map((parte) =>
    /^\d+$/.test(parte) ? Number.parseInt(parte, 10) : Number.NaN,
  );
  if (numeros.some((numero) => !Number.isInteger(numero) || numero < 1)) {
    return null;
  }
  if (numeros[0] + numeros[1] + numeros[2] !== XI_SIZE - 1) return null;

  const counts: Record<Position, number> = {
    POR: 1,
    DEF: numeros[0],
    MED: numeros[1],
    DEL: numeros[2],
  };

  return {
    raw: raw.trim(),
    counts,
    lines: POSITIONS.map((position) => ({ position, count: counts[position] })),
  };
}
